import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatCard } from '../../components/common/StatCard';
import { useStaggeredEntrance } from '../../hooks/useStaggeredEntrance';
import {
  MOCK_ACTIVITY,
  MOCK_ADMIN_STATS,
  MOCK_LEADERBOARD,
  MOCK_QUEUES,
  QUEUE_ORDER,
} from '../../mocks';
import { ForgeQueue, ForgeQueueGroup, ForgeQueueHealth } from '../../types';

// ===== Queue grouping logic =====
function worstHealth(a: ForgeQueueHealth, b: ForgeQueueHealth): ForgeQueueHealth {
  const rank: Record<ForgeQueueHealth, number> = { critical: 0, attention: 1, healthy: 2 };
  return rank[a] <= rank[b] ? a : b;
}

function groupQueues(queues: ForgeQueue[]): ForgeQueueGroup[] {
  const map = new Map<string, ForgeQueueGroup>();
  for (const q of queues) {
    const existing = map.get(q.queueName);
    if (existing) {
      existing.totalTasks += q.taskCount;
      existing.totalClaimed += q.claimedCount;
      existing.worstHealth = worstHealth(existing.worstHealth, q.health);
      existing.chains.push(q);
    } else {
      map.set(q.queueName, {
        queueName: q.queueName,
        displayName: q.displayName,
        totalTasks: q.taskCount,
        totalClaimed: q.claimedCount,
        worstHealth: q.health,
        chains: [q],
      });
    }
  }
  return [...map.values()].sort(
    (a, b) => (QUEUE_ORDER[a.queueName] ?? 99) - (QUEUE_ORDER[b.queueName] ?? 99)
  );
}

// ===== Time ago helper =====
function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ===== Queue Health Table =====
const QueueHealthTable: React.FC = () => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const groups = groupQueues(MOCK_QUEUES);
  const navigate = useNavigate();

  const toggle = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleStart = (e: React.MouseEvent, queueName: string) => {
    e.stopPropagation();
    navigate(`/forge/tasks/player?queue=${encodeURIComponent(queueName)}`);
  };

  return (
    <table className="qh-table">
      <thead>
        <tr>
          <th className="col-chevron" />
          <th>Queue</th>
          <th className="text-right">Tasks</th>
          <th className="text-right">Claimed</th>
          <th>Status</th>
          <th className="col-play" />
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => {
          const isExpanded = expanded.has(group.queueName);
          return (
            <React.Fragment key={group.queueName}>
              {/* Group header row */}
              <tr className="qh-group-row" onClick={() => toggle(group.queueName)}>
                <td className="qh-chevron">
                  {group.chains.length > 1 ? (
                    isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />
                  ) : (
                    <span style={{ width: 14 }} />
                  )}
                </td>
                <td>
                  <div className="qh-queue-name">{group.displayName}</div>
                  <div className="qh-chain-count">
                    {group.chains.length} chain{group.chains.length > 1 ? 's' : ''}
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className="qh-mono">{group.totalTasks}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className="qh-mono-secondary">{group.totalClaimed}</span>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`forge-health-dot ${group.worstHealth}`} />
                    <span className="forge-health-label">{group.worstHealth}</span>
                  </div>
                </td>
                <td>
                  <button
                    className="qh-play-btn"
                    onClick={(e) => handleStart(e, group.queueName)}
                    title={`Start working on ${group.displayName}`}
                  >
                    <PlayArrowIcon />
                  </button>
                </td>
              </tr>

              {/* Expanded chain sub-rows */}
              {isExpanded &&
                group.chains.map((q) => (
                  <tr key={`${q.queueName}-${q.chainId}`} className="qh-chain-row">
                    <td />
                    <td className="qh-chain-name">{q.chainName}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="qh-mono">{q.taskCount}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="qh-mono-secondary">{q.claimedCount}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className={`forge-health-dot ${q.health}`} />
                        <span className={`forge-priority-badge ${q.priorityClass}`}>
                          {q.priorityClass}
                        </span>
                      </div>
                    </td>
                    <td />
                  </tr>
                ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

// ===== Main Home Page =====
export const ForgeHomePage: React.FC = () => {
  const navigate = useNavigate();
  const visibility = useStaggeredEntrance(7, 50);

  const handleStartWorking = () => {
    navigate('/forge/tasks/player');
  };

  return (
    <div className="page-content">
      {/* Stats Row */}
      <div className="stats-row">
        {MOCK_ADMIN_STATS.map((stat, i) => (
          <StatCard key={stat.label} stat={stat} isVisible={visibility[i]} />
        ))}
      </div>

      {/* Start Working CTA */}
      <div style={{ marginTop: 28, marginBottom: 28, opacity: visibility[4] ? 1 : 0, transform: visibility[4] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 350ms ease, transform 350ms ease' }}>
        <button className="btn-start" onClick={handleStartWorking}>
          <PlayArrowIcon />
          Start Working
        </button>
        <span className="btn-start-hint">All queues</span>
      </div>

      {/* Two-column layout */}
      <div className="home-grid">
        {/* Left: Queue Health */}
        <div style={{ opacity: visibility[5] ? 1 : 0, transform: visibility[5] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 350ms ease, transform 350ms ease' }}>
          <div className="forge-card">
            <div className="forge-card-title">Queue Health</div>
            <QueueHealthTable />
          </div>
        </div>

        {/* Right column */}
        <div className="home-right-col">
          {/* Leaderboard */}
          <div style={{ opacity: visibility[5] ? 1 : 0, transform: visibility[5] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 350ms ease, transform 350ms ease' }}>
            <div className="forge-card">
              <div className="forge-card-title">
                <PeopleOutlinedIcon /> Labeler Leaderboard
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {MOCK_LEADERBOARD.map((l, i) => (
                  <div key={l.id} className="lb-row">
                    <span className="lb-rank">{i + 1}</span>
                    <span className={`lb-dot ${l.status}`} />
                    <span className="lb-name">{l.name}</span>
                    <span className="lb-tasks">{l.tasksToday}</span>
                    <span className="lb-accuracy">{Math.round(l.accuracy * 100)}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Team Activity */}
          <div style={{ opacity: visibility[6] ? 1 : 0, transform: visibility[6] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 350ms ease, transform 350ms ease' }}>
            <div className="forge-card">
              <div className="forge-card-title">
                <TimelineOutlinedIcon /> Team Activity
              </div>
              <div className="af-container">
                <div className="af-line" />
                {MOCK_ACTIVITY.map((item) => (
                  <div key={item.id} className="af-item">
                    <div className={`af-dot ${item.type}`} />
                    <div className="af-body">
                      <div className="af-text">
                        {item.description}
                        {item.entityId && <span className="af-entity">{item.entityId}</span>}
                      </div>
                      <div className="af-time">{timeAgo(item.timestamp)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
